import type { FlatNamespace, KeyPrefix, Namespace, TFunction } from "i18next";
import { useMemo } from "react";
import type { FallbackNs, UseTranslationOptions, UseTranslationResponse } from "react-i18next";
import { useTranslation } from "react-i18next";
import { z } from "zod";

import { withInvertedProperties } from "../record";
import { capitalize, EMPTY, PERIOD, SLASH, UNDERSCORE } from "../str";
import { isOneOf } from "../type-guard";

import { DEFAULT_LANGUAGE_CODE, LanguageCode } from "./language";
import type { Animateness, Case, Context, Gender, PartOfSpeech, Plural } from "./modifiers";
import type { Tk } from "./translation-key";

type Tuple<T> = readonly [T?, ...T[]];

type TFunctionWithPostprocessor<
  Ns extends FlatNamespace | Tuple<FlatNamespace> | undefined = undefined,
  KPrefix extends KeyPrefix<FallbackNs<Ns>> = undefined,
  Result extends string = string,
> = (...args: Parameters<TFunction<FallbackNs<Ns>, KPrefix>>) => Result;

function useTranslation2<
  Ns extends FlatNamespace | Tuple<FlatNamespace> | undefined = undefined,
  KPrefix extends KeyPrefix<FallbackNs<Ns>> = undefined,
>(
  ns?: Ns,
  options?: UseTranslationOptions<KPrefix>,
): {
  translation: UseTranslationResponse<FallbackNs<Ns>, KPrefix>;
  postproc: {
    tc: TFunctionWithPostprocessor<FallbackNs<Ns>, KPrefix, Capitalize<string>>;
    ts: (punctuationMark: string) => TFunctionWithPostprocessor<FallbackNs<Ns>, KPrefix>;
    tsp: TFunctionWithPostprocessor<FallbackNs<Ns>, KPrefix>;
  };
} {
  const translation = useTranslation(ns, options);

  const { t } = translation;

  const postproc = useMemo(
    () => ({
      tc: createTc(t),
      ts: createTs(t),
      tsp: createTsp(t),
    }),
    [t],
  );

  return {
    translation,
    postproc,
  };
}

function createTc<Ns extends Namespace, KPrefix>(t: TFunction<Ns, KPrefix>) {
  return (...args: Parameters<typeof t>) => capitalize(t(...args));
}

function createTs<Ns extends Namespace, KPrefix>(t: TFunction<Ns, KPrefix>) {
  return (punctuationMark: string) =>
    (...args: Parameters<typeof t>) =>
      [createTc(t)(...args), punctuationMark].join(EMPTY);
}

function createTsp<Ns extends Namespace, KPrefix>(t: TFunction<Ns, KPrefix>) {
  return (...args: Parameters<typeof t>) => createTs(t)(PERIOD)(...args);
}

function tk<
  P extends Plural,
  PS extends PartOfSpeech,
  C extends Case,
  Ctx extends Context,
  A extends Animateness,
  G extends Gender,
>(
  value: string,
  modifiers?: Partial<{
    plural: P;
    partOfSpeech: PS;
    case_: C;
    context: Ctx;
    animateness: A;
    gender: G;
  }>,
) {
  const {
    plural = EMPTY,
    partOfSpeech = EMPTY,
    case_ = EMPTY,
    context = EMPTY,
    animateness = EMPTY,
    gender = EMPTY,
  } = modifiers ?? {};
  return [value, partOfSpeech, case_, context, animateness, gender, plural]
    .filter((value) => value)
    .join(UNDERSCORE);
}

function pluralForSlavic(key: Tk, value: string, modifiers?: Parameters<typeof tk>[1]) {
  return Object.fromEntries(
    (["few", "many", "other"] satisfies Plural[]).map(
      (plural) =>
        [
          tk(key, {
            plural,
            ...modifiers,
          }),
          value,
        ] as [string, string],
    ),
  );
}

const zDayOfWeek = z.enum([
  "SUNDAY",
  "MONDAY",
  "TUESDAY",
  "WEDNESDAY",
  "THURSDAY",
  "FRIDAY",
  "SATURDAY",
]);
const DayOfWeek = zDayOfWeek.Values;
type DayOfWeek = z.infer<typeof zDayOfWeek>;

const DAY_NUMBER_BY_DAY_OF_WEEK = Object.fromEntries(
  Object.keys(DayOfWeek).map((value, index) => [value, index]),
) as Record<DayOfWeek, number>;

const SUNDAY = new Date(1970, 0, 4);

function getNameOfDayOfWeek(
  locale: string,
  day: DayOfWeek,
  style: NonNullable<ConstructorParameters<typeof Intl.DateTimeFormat>[1]>["weekday"] = "short",
) {
  const date = new Date(SUNDAY);
  date.setDate(date.getDate() + DAY_NUMBER_BY_DAY_OF_WEEK[day]);

  return new Intl.DateTimeFormat(locale, {
    weekday: style,
  }).format(date);
}

const zMonth = z.enum([
  "JANUARY",
  "FEBRUARY",
  "MARCH",
  "APRIL",
  "MAY",
  "JUNE",
  "JULY",
  "AUGUST",
  "SEPTEMBER",
  "OCTOBER",
  "NOVEMBER",
  "DECEMBER",
]);
const Month = zMonth.Enum;
type Month = z.infer<typeof zMonth>;

const MONTH_BY_NUMBER = Object.fromEntries(
  Object.values(Month).map((value, index) => [index, value]),
) as Record<number, Month>;

const NUMBER_BY_MONTH = withInvertedProperties(MONTH_BY_NUMBER);

function getNameOfMonth(
  locale: string,
  month: Month,
  style: NonNullable<ConstructorParameters<typeof Intl.DateTimeFormat>[1]>["month"] = "short",
) {
  const date = new Date(1970, 0, 1);
  date.setMonth(date.getMonth() + NUMBER_BY_MONTH[month]);

  return new Intl.DateTimeFormat(locale, {
    month: style,
  }).format(date);
}

function formatDate(language: string, date: Date | number) {
  return new Intl.DateTimeFormat(language, {
    dateStyle: "medium",
  }).format(date);
}

function formatDateTime(language: string, date: Date | number) {
  return new Intl.DateTimeFormat(language, {
    dateStyle: "medium",
    timeStyle: "medium",
  }).format(date);
}

function formatCurrency(value: number) {
  return new Intl.NumberFormat("de-CH", {
    style: "currency",
    currency: "CHF",
  }).format(value);
}

function getLanguage(candidate?: string) {
  return isOneOf(candidate, Object.values(LanguageCode)) ? candidate : DEFAULT_LANGUAGE_CODE;
}

function extractLanguage(url: string) {
  const [, language] = new URL(url).pathname.split(SLASH);

  return getLanguage(language);
}

export {
  createTc,
  createTs,
  createTsp,
  DayOfWeek,
  extractLanguage,
  formatCurrency,
  formatDate,
  formatDateTime,
  getLanguage,
  getNameOfDayOfWeek,
  getNameOfMonth,
  Month,
  MONTH_BY_NUMBER,
  NUMBER_BY_MONTH,
  pluralForSlavic,
  tk,
  useTranslation2,
};
